import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { inject, observer } from "mobx-react";
import officialLogo from "../../img/official_logo.svg";
import UISwitch from "../../components/UISwitch";
import AccountMenu from "./AccountMenu";
import { releaseNotesEnabled } from "./releaseNotesFlag";
import { getConfig } from "../../lib/configMgr";

// Resolved once at module load (mirrors WorkspaceApp). OFF → plain avatar (today's
// behavior); ON (wherever v2 is on) → the v2 account dropdown.
const RELEASE_NOTES_ON = releaseNotesEnabled();

// The persistent "Beta" badge is gated on the same v2 enable flag the routes
// use (see index.js / NavBar.js). It isn't tied to any first-run state — it
// stays put on every v2 route while the surface is in preview.
const WITH_V2 = (() => { try { return !!getConfig().withV2; } catch (_) { return false; } })();

const Crumb = ({ to, children, last }) => {
  if (last) {
    return <span className="workspace-navbar__crumb workspace-navbar__crumb--current">{children}</span>;
  }
  return (
    <Link className="workspace-navbar__crumb" to={to}>{children}</Link>
  );
};

const WorkspaceNavBarBase = ({ workspaceStore, userStore, programId, projectId, chatId, onOpenSearch }) => {
  const navigate = useNavigate();
  const program = programId ? workspaceStore.getProgramById(programId) : null;
  const programLabel = program?.name || programId || null;
  const projects = programId ? workspaceStore.getProjectsForProgram(programId) : [];
  const project = projectId ? projects.find((p) => p.id === projectId) : null;
  const projectLabel = project?.name || projectId || null;

  return (
    <nav className="workspace-navbar" data-testid="v2-navbar">
      <Link className="workspace-navbar__brand" to="/v2" data-testid="v2-navbar-brand" aria-label="Plex — back to workspace">
        <img
          src={officialLogo}
          alt="Plex"
          className="workspace-navbar__brand-logo"
          width={64}
          height={30}
        />
      </Link>

      {WITH_V2 && (
        <span
          className="workspace-navbar__beta"
          data-testid="v2-beta-badge"
          role="note"
          aria-label="Beta — the updated Plex experience is a preview release"
          title="Beta — the updated Plex experience is a preview release"
        >
          Beta
        </span>
      )}

      <div className="workspace-navbar__crumbs" data-testid="v2-navbar-crumbs">
        <Crumb to="/v2" last={!programId}>Workspace</Crumb>
        {programLabel && (
          <>
            <span className="workspace-navbar__sep">/</span>
            <Crumb to={`/v2/p/${programId}`} last={!projectId}>{programLabel}</Crumb>
          </>
        )}
        {projectLabel && (
          <>
            <span className="workspace-navbar__sep">/</span>
            <Crumb to={`/v2/p/${programId}/${projectId}`} last={!chatId}>{projectLabel}</Crumb>
          </>
        )}
        {chatId && (
          <>
            <span className="workspace-navbar__sep">/</span>
            <span className="workspace-navbar__crumb workspace-navbar__crumb--current">chat</span>
          </>
        )}
      </div>

      <div className="workspace-navbar__spacer" />

      {programId && projects.length > 0 && (
        <select
          className="workspace-navbar__switcher"
          data-testid="v2-navbar-project-switcher"
          value={projectId || ""}
          onChange={(e) => {
            const next = e.target.value;
            if (next) navigate(`/v2/p/${programId}/${next}`);
          }}
        >
          <option value="">Switch project…</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      )}

      <select
        className="workspace-navbar__switcher"
        data-testid="v2-navbar-program-switcher"
        value={programId || ""}
        onChange={(e) => {
          const next = e.target.value;
          if (next) navigate(`/v2/p/${next}`);
          else navigate("/v2");
        }}
      >
        <option value="">Switch program…</option>
        {(workspaceStore.programs || []).map((p) => (
          <option key={p.id} value={p.id}>{p.name}</option>
        ))}
      </select>

      <button
        type="button"
        className="workspace-navbar__icon-btn"
        title="Search (Ctrl/⌘+K)"
        data-testid="v2-navbar-search"
        onClick={() => onOpenSearch?.()}
      >
        🔍
      </button>

      {/* Always visible for now; swap to per-user v2-access gating once that entitlement exists */}
      <UISwitch current="v2" />

      {RELEASE_NOTES_ON ? (
        <AccountMenu />
      ) : (
        <span className="workspace-navbar__avatar" data-testid="v2-navbar-avatar" title={userStore?.userName || "Account"}>
          {(userStore?.userName || "?").slice(0, 1).toUpperCase()}
        </span>
      )}
    </nav>
  );
};

export const WorkspaceNavBar = inject("workspaceStore", "userStore")(observer(WorkspaceNavBarBase));
export default WorkspaceNavBar;
